import { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { CreateInvoicePermission } from 'src/components/guards/PermissionsList';
import Layout from 'src/components/Layout';
import LoadingPanel from 'src/components/LoadingPanel';
import ShowScansSplitter from 'src/components/scanner/ShowScansSplitter';
import usePermission from 'src/hooks/usePermissions';
import InvoiceDetailsRelatedDocuments from 'src/sections/invoice_details/InvoiceDetailsRelatedDocuments';
import InvoiceInfo from 'src/sections/invoice_details/InvoiceInfo';
import { PoRelatedDocumentsContext } from 'src/sections/po_related_documents/PoRelatedDocumentsContext';
import { isEqualString } from 'src/utils/strings';
import { useContextSelector } from 'use-context-selector';
import InvoiceDetailsActions from './InvoiceDetailsActions';
import { InvoiceContext } from './InvoiceDetailsContext';
import InvoiceDetailsGLDistribution from './InvoiceDetailsGLDistribution';
import InvoiceDetailsNavBar from './InvoiceDetailsNavBar';
import InvoiceLineItems from './InvoiceLineItems';
import MultiPeriodAccounting from './MultiPeriodAccounting';

const InvoiceScans = () => {
  const showScans = useContextSelector(InvoiceContext, (value) => value.showScans);
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const isInvoiceLoading = useContextSelector(InvoiceContext, (value) => value.isInvoiceLoading);
  const getInvoice = useContextSelector(InvoiceContext, (value) => value.getInvoice);
  const invoiceId = useContextSelector(InvoiceContext, (value) => value.invoiceId);
  const isInvoiceDirty = useContextSelector(InvoiceContext, (value) => value.isInvoiceDirty);
  const invoiceCreatedBy = useContextSelector(
    PoRelatedDocumentsContext,
    (value) => value?.poDetails?.buyerId
  )?.toString?.();

  const saveInvoice = useContextSelector(InvoiceContext, (value) => value.saveInvoice);
  const title = useContextSelector(InvoiceContext, (value) => value.title);
  const isInitiallyLoading = useContextSelector(
    InvoiceContext,
    (value) => value.isInitiallyLoading
  );

  const singleGlPeriodStartDate = useContextSelector(
    InvoiceContext,
    (value) => value.singleGlPeriodStartDate
  );

  const hasErrors = useContextSelector(InvoiceContext, (value) => value.budgetPeriodsHasErrors);
  const poStatus = useContextSelector(
    PoRelatedDocumentsContext,
    (value) => value?.poDetails?.poStatus
  );
  const isPoOpen = isEqualString(poStatus, 'open');

  const saveAndGetInvoice = useCallback(async () => {
    try {
      if (isInvoiceDirty && !hasErrors) {
        await saveInvoice(singleGlPeriodStartDate);
        toast.success(`${title} saved successfully!`);
      }
    } catch (error) {
      console.error(error);
    } finally {
      await getInvoice();
    }
  }, [getInvoice, hasErrors, isInvoiceDirty, saveInvoice, singleGlPeriodStartDate, title]);

  const hasInvoiceAccessPermission = usePermission({
    permissions: CreateInvoicePermission,
    or: true,
    clientId: invoiceCreatedBy,
  });

  return (
    <main className="min-h-screen">
      <InvoiceDetailsNavBar />
      <Layout>
        {isInitiallyLoading ? (
          <LoadingPanel id="InvoiceDetail" />
        ) : hasInvoiceAccessPermission ? (
          <>
            <InvoiceDetailsActions />
            <ShowScansSplitter
              isInvoice={true}
              document={invoice}
              isLoading={isInvoiceLoading}
              companyId={invoice?.companyId?.toString()}
              getDocument={saveAndGetInvoice}
              id={invoiceId}
              showScans={showScans}
              isPoOpen={isPoOpen}
              main={
                <main className="flex p-1 flex-col gap-2">
                  <InvoiceDetailsRelatedDocuments isDirty={isInvoiceDirty} />
                  <InvoiceInfo />
                  <InvoiceDetailsGLDistribution />
                  <InvoiceLineItems />
                  <MultiPeriodAccounting />
                </main>
              }
            />
          </>
        ) : (
          <div className="flex h-screen">
            <div className="m-auto">
              <p>You are not authorized to view this document</p>
            </div>
          </div>
        )}
      </Layout>
      <Tooltip
        id="ocr-tooltip"
        place="bottom"
        style={{
          backgroundColor: 'white',
          color: 'black',
          zIndex: 999999,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
          opacity: 1.0,
        }}
      />
    </main>
  );
};

export default memo(InvoiceScans);
